import { AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { RemitReportingSubscriptionInfo } from 'src/apis/remit-reporting/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { handleRequestError } from 'src/utils/axios-config';

export type ReportingSubscriptionTradeType = 'PRIMARY' | 'SECONDARY';

export function useRemitReportingSubscriptionInfo(
  tradeType: ReportingSubscriptionTradeType
) {
  const subscriptionInfo = useAxios(
    (axios, signal) =>
      axios
        .request<RemitReportingSubscriptionInfo>({
          signal,
          url: `${
            PRISMA_CONFIG.remitReporting
          }/api/platform/remit-reporting-settings/${tradeType.toLowerCase()}`,
          method: 'GET',
          timeout: 5000,
        })
        .catch((err) => {
          if (isServerError(err, 403)) {
            return {
              data: undefined,
              status: err.status!,
              statusText: '',
              headers: {},
              config: err.config,
            } as AxiosResponse<undefined>;
          }
          throw err;
        }),
    {
      neededOnPageLoad: false,
      onError(error) {
        const [_, report] = handleRequestError(error, () => {});

        if (report) reportError(error);

        return error;
      },
      cacheKey: `RemitReportingSubscriptionInfo-${tradeType}`,
    }
  );

  const { execute, setResponse, refresh, ...restSubscriptionInfo } =
    subscriptionInfo;

  useEffect(() => {
    if (PRISMA_CONFIG.remitReporting != null) execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (PRISMA_CONFIG.remitReporting == null) return;

  return restSubscriptionInfo;
}

export type RemitReportingSubscriptionInfoRequest = ReturnType<
  typeof useRemitReportingSubscriptionInfo
>;

export const remitReportingSubscriptionInfoLoading = (
  infoRequest: RemitReportingSubscriptionInfoRequest
) => (infoRequest ? !infoRequest.loaded : false);

export const isRemitReportingSubscriptionInfoLoaded = (
  infoRequest: RemitReportingSubscriptionInfoRequest
) => !!infoRequest?.response?.data;

export const isRemitReportingSubscriptionActive = (
  infoRequest: RemitReportingSubscriptionInfoRequest
) => !!infoRequest?.response?.data?.active;
